import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Loader2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Backfill a purchase that happened BEFORE inventory tracking existed.
 * `target` = { type: 'product'|'equipment', id, name }
 */
const LogPastPurchaseDialog = ({ target, onOpenChange, headers, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (target) setForm({
      quantity: '', amount_paid: '', purchase_date: '',
      vendor_name: '', store_address: '', purchase_url: '', notes: '',
      add_to_current_stock: false,
    });
  }, [target]);

  if (!target) return null;

  const save = async () => {
    if (!form.quantity || !form.amount_paid || !form.purchase_date) {
      toast.error('Quantity, amount, and purchase date are required'); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: parseFloat(form.quantity),
        amount_paid: parseFloat(form.amount_paid),
      };
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] == null) delete payload[k]; });
      const path = target.type === 'equipment' ? 'equipment' : 'products';
      await axios.post(`${API_URL}/api/admin/inventory/${path}/${target.id}/log-past-purchase`, payload, { headers });
      toast.success('Past purchase logged');
      onSaved(); onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="log-past-dialog">
        <DialogHeader>
          <DialogTitle>Log past purchase — {target.name}</DialogTitle>
          <DialogDescription>Backfill a purchase from before inventory tracking existed. Uses the actual historical date, not today.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <F label="Quantity *"><Input type="number" step="1" value={form.quantity} onChange={e => set('quantity', e.target.value)} data-testid="past-qty" /></F>
            <F label="Amount Paid ($) *"><Input type="number" step="0.01" value={form.amount_paid} onChange={e => set('amount_paid', e.target.value)} data-testid="past-amount" /></F>
          </div>
          <F label="Purchase Date * (historical)"><Input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} data-testid="past-date" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Vendor"><Input value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} /></F>
            <F label="Store Address"><Input value={form.store_address} onChange={e => set('store_address', e.target.value)} /></F>
          </div>
          <F label="Purchase URL"><Input value={form.purchase_url} onChange={e => set('purchase_url', e.target.value)} /></F>
          <F label="Notes"><Textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></F>
          {target.type === 'product' && (
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <Checkbox checked={form.add_to_current_stock} onCheckedChange={v => set('add_to_current_stock', !!v)} data-testid="past-add-to-stock" />
              <span>Add this quantity to current inventory. Leave unchecked if this stock has already been used up.</span>
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#F56F05] hover:bg-[#e06505] text-white" data-testid="past-save">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Log purchase
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
const F = ({ label, children }) => (<div className="space-y-1"><Label className="text-xs text-zinc-600">{label}</Label>{children}</div>);

export default LogPastPurchaseDialog;
